// Feature module router for control panel
// Requirements: 5.3, 5.4

import React, { useEffect } from 'react';
import { FeatureModule } from './ControlPanel';
import { useAuth } from '../../contexts/AuthContext';
import ERPDashboard from './ERPDashboard';
import NodeManagement from '../NodeManagement/NodeManagement';
import InventoryManagement from '../InventoryManagement/InventoryManagement';
import OptimizationEngine from '../OptimizationEngine/OptimizationEngine';
import SOPPlanning from '../SOPPlanning/SOPPlanning';
import './FeatureRouter.css';

interface FeatureRouterProps {
  activeModule: FeatureModule;
  onModuleChange: (module: FeatureModule) => void;
}

export const FeatureRouter: React.FC<FeatureRouterProps> = ({
  activeModule,
  onModuleChange
}) => {
  const { state: authState } = useAuth();

  // Maintain session context across navigation
  useEffect(() => {
    // Store current module in session storage for persistence
    if (authState.isAuthenticated) {
      sessionStorage.setItem('activeModule', activeModule);
      
      // Update global context for AI Assistant
      (window as any).currentActiveView = activeModule;
      (window as any).userContext = {
        userId: authState.user?.user_id,
        tenantId: authState.user?.tenant_id,
        email: authState.user?.email,
        role: authState.user?.role
      };
    }
  }, [activeModule, authState]);

  // Restore active module from session storage on mount
  useEffect(() => {
    if (authState.isAuthenticated) {
      const savedModule = sessionStorage.getItem('activeModule') as FeatureModule;
      if (savedModule && savedModule !== activeModule) {
        onModuleChange(savedModule);
      }
    }
  }, [authState.isAuthenticated, onModuleChange, activeModule]);

  // Clear session data on logout
  useEffect(() => {
    if (!authState.isAuthenticated) {
      sessionStorage.removeItem('activeModule');
      delete (window as any).currentActiveView;
      delete (window as any).userContext;
    }
  }, [authState.isAuthenticated]);

  const renderFeatureModule = () => {
    // Ensure user is authenticated before rendering any feature
    if (!authState.isAuthenticated || !authState.user) {
      return (
        <div className="feature-router__error">
          <div className="error-content">
            <h2>Authentication Required</h2>
            <p>Please log in to access this feature.</p>
          </div>
        </div>
      );
    }

    // Add tenant context to all feature modules (optional for backward compatibility)
    const featureProps = {
      tenantId: authState.user.tenant_id,
      userId: authState.user.user_id,
      userRole: authState.user.role
    };

    switch (activeModule) {
      case 'dashboard':
        return (
          <div className="feature-module" data-module="dashboard">
            <ERPDashboard onModuleChange={onModuleChange} />
          </div>
        );
      
      case 'network':
        return (
          <div className="feature-module" data-module="network">
            <NodeManagement />
          </div>
        );
      
      case 'inventory':
        return (
          <div className="feature-module" data-module="inventory">
            <InventoryManagement />
          </div>
        );
      
      case 'optimize':
        return (
          <div className="feature-module" data-module="optimize">
            <OptimizationEngine />
          </div>
        );
      
      case 'planning':
        return (
          <div className="feature-module" data-module="planning">
            <SOPPlanning />
          </div>
        );
      
      default:
        return (
          <div className="feature-router__error">
            <div className="error-content">
              <h2>Feature Not Found</h2>
              <p>The requested feature module could not be found.</p>
              <button 
                className="btn btn-primary"
                onClick={() => onModuleChange('dashboard')}
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="feature-router">
      <div className="feature-content">
        {renderFeatureModule()}
      </div>
    </div>
  );
};

export default FeatureRouter;